import './components/login/login.component.js';
import './components/client-list/client-list.component.js';
import './components/client-detail/client-detail.component.js';
import './components/user-list/user-list.component.js';
import './components/notification/notification.component.js';
import './components/empty-state/empty-state.component.js';
import './components/confirm-dialog/confirm-dialog.component.js';
import './components/billing-overview/billing-overview.component.js';
import './components/mark-invoiced-dialog/mark-invoiced-dialog.component.js';
import './components/client-invoice-dialog/client-invoice-dialog.component.js';

import { ClientApiAdapter } from './shell/adapters/driven/client.api-adapter.js';
import { AuthApiAdapter } from './shell/adapters/driven/auth.api-adapter.js';
import { ClientListEventAdapter } from './shell/adapters/driving/client-list.event-adapter.js';
import { ClientDetailEventAdapter } from './shell/adapters/driving/client-detail.event-adapter.js';
import { UserListEventAdapter } from './shell/adapters/driving/user-list.event-adapter.js';
import { BillingOverviewEventAdapter } from './shell/adapters/driving/billing-overview.event-adapter.js';
import { PlatformCostsEventAdapter } from './shell/adapters/driving/platform-costs.event-adapter.js';
import { MarkInvoicedEventAdapter } from './shell/adapters/driving/mark-invoiced.event-adapter.js';
import { ClientInvoiceEventAdapter } from './shell/adapters/driving/client-invoice.event-adapter.js';
import { navBadgePresentation } from './core/billing/billing.core.js';
import { ClipboardEventAdapter } from './shell/adapters/driving/clipboard.event-adapter.js';
import { RouteGuard } from '@web-builder/shared/route-guard.js';

const API_BASE = window.__API_BASE__ || '/api/admin';
const AUTH_BASE = API_BASE.replace(/\/admin\/?$/, '/auth/admin');
const BILLING_ROUTE = '#/billing';
const CLIENT_DETAIL_ROUTE = /^#\/clients\/(.+)$/;

export function bootstrap() {
  const loginEl = document.querySelector('admin-login');
  const appEl = document.querySelector('#app');
  const navEl = document.querySelector('#nav');
  const menuBtn = document.querySelector('#btn-menu');
  const listEl = document.querySelector('client-list');
  const detailEl = document.querySelector('client-detail');
  const userListEl = document.querySelector('user-list');
  const billingEl = document.querySelector('billing-overview');
  const markInvoicedEl = document.querySelector('mark-invoiced-dialog');
  const clientInvoiceEl = document.querySelector('client-invoice-dialog');
  const billingBadgeEl = document.querySelector('#billing-badge');
  const notificationEl = document.querySelector('admin-notification');

  const views = [listEl, detailEl, userListEl, billingEl];

  let listAdapter;
  let detailAdapter;
  let userAdapter;
  let billingAdapter;
  let platformCostsAdapter;
  let markInvoicedAdapter;
  let clientInvoiceAdapter;
  let clipboardAdapter;
  /** API adapter of the signed-in session; a summary answer for an earlier session is dropped. */
  let sessionApi = null;
  /**
   * Leaving a client detail route drops an unsaved template edit, and leaving the billing route an
   * unsaved "Add item" form, so those ask first. Declining returns to the history entry of the
   * route on screen, so the history holds no extra entry and Back keeps working as before.
   */
  const routeGuard = new RouteGuard({
    mayLeave: async (currentHash) => {
      if (CLIENT_DETAIL_ROUTE.test(currentHash)) {
        return detailEl.discardChangesIfConfirmed();
      }
      return currentHash !== BILLING_ROUTE || billingEl.addItem.discardChangesIfConfirmed();
    },
    show: showRoute,
  });
  let menuClickHandler;
  let documentNavClickHandler;
  let logoutClickHandler;
  let destroyed = false;
  let authAttempt = 0;
  const authApi = new AuthApiAdapter(AUTH_BASE);

  function showOnly(el) {
    views.forEach((v) => (v.style.display = v === el ? '' : 'none'));
  }

  function updateNav(hash) {
    navEl.querySelectorAll('a').forEach((a) => {
      const active = a.getAttribute('href') === hash || (hash.startsWith('#/clients/') && a.getAttribute('href') === '#/');
      a.classList.toggle('active', active);
      if (active) {
        a.setAttribute('aria-current', 'page');
      } else {
        a.removeAttribute('aria-current');
      }
    });
  }

  /**
   * The one place the nav badge is read: the count of items that need an invoice, red when any is
   * overdue, amber when only due soon, hidden at zero. A failed load hides it, since a stale count
   * is worse than none.
   */
  async function refreshBillingBadge() {
    const api = sessionApi;
    let summary = null;
    try {
      summary = await api.getBillingSummary();
    } catch {
      // The badge is only a hint; the overview page reports load failures itself.
    }
    if (api === sessionApi) {
      renderBillingBadge(summary);
    }
  }

  /** @param {{ overdue: number, dueSoon: number } | null} summary null hides the badge */
  function renderBillingBadge(summary) {
    const { count, badgeClass, label } = navBadgePresentation(summary);
    billingBadgeEl.className = badgeClass ? `nav-badge ${badgeClass}` : 'nav-badge hidden';
    billingBadgeEl.textContent = count === 0 ? '' : String(count);
    if (label) {
      billingBadgeEl.setAttribute('aria-label', label);
      billingBadgeEl.title = label;
    } else {
      billingBadgeEl.removeAttribute('aria-label');
      billingBadgeEl.removeAttribute('title');
    }
  }

  async function activate(credentials) {
    const attempt = ++authAttempt;
    loginEl.setLoading(true);
    let apiAdapter;
    try {
      const { token } = await authApi.login(credentials);
      if (destroyed || attempt !== authAttempt) return;
      apiAdapter = new ClientApiAdapter(API_BASE, token);
      await apiAdapter.getAllClients();
    } catch (error) {
      if (!destroyed && attempt === authAttempt) {
        notificationEl.show(error.status === 401 ? 'Invalid email or password' : `Could not sign in: ${error.message}`, 'error');
      }
      return;
    } finally {
      if (!destroyed && attempt === authAttempt) loginEl.setLoading(false);
    }

    if (destroyed || attempt !== authAttempt) return;

    if (listAdapter || detailAdapter || userAdapter || billingAdapter || platformCostsAdapter || markInvoicedAdapter || clientInvoiceAdapter) {
      deactivate();
    }

    loginEl.style.display = 'none';
    appEl.classList.remove('hidden');

    listAdapter = new ClientListEventAdapter(apiAdapter, listEl, notificationEl);
    detailAdapter = new ClientDetailEventAdapter(apiAdapter, detailEl, notificationEl);
    userAdapter = new UserListEventAdapter(apiAdapter, userListEl, notificationEl);
    billingAdapter = new BillingOverviewEventAdapter(apiAdapter, billingEl, notificationEl);
    platformCostsAdapter = new PlatformCostsEventAdapter(apiAdapter, billingEl.platformCosts, notificationEl);
    markInvoicedAdapter = new MarkInvoicedEventAdapter(apiAdapter, markInvoicedEl, notificationEl);
    clientInvoiceAdapter = new ClientInvoiceEventAdapter(apiAdapter, clientInvoiceEl, notificationEl);
    clipboardAdapter = new ClipboardEventAdapter(navigator.clipboard, notificationEl);
    sessionApi = apiAdapter;
    listAdapter.connect();
    detailAdapter.connect();
    userAdapter.connect();
    billingAdapter.connect();
    platformCostsAdapter.connect();
    markInvoicedAdapter.connect();
    clientInvoiceAdapter.connect();
    clipboardAdapter.connect();
    loginEl.reset();

    menuClickHandler = () => {
      const open = navEl.classList.toggle('open');
      menuBtn.classList.toggle('open', open);
      menuBtn.setAttribute('aria-expanded', String(open));
    };

    documentNavClickHandler = (e) => {
      if (!navEl.contains(e.target) && e.target !== menuBtn && !menuBtn.contains(e.target)) {
        navEl.classList.remove('open');
        menuBtn.classList.remove('open');
        menuBtn.setAttribute('aria-expanded', 'false');
      }
    };

    logoutClickHandler = async () => {
      if (await detailEl.discardChangesIfConfirmed() && await billingEl.addItem.discardChangesIfConfirmed()) {
        deactivate();
      }
    };

    menuBtn.addEventListener('click', menuClickHandler);
    document.addEventListener('click', documentNavClickHandler);

    document.querySelector('#btn-logout').addEventListener('click', logoutClickHandler);
    document.addEventListener('billing-changed', refreshBillingBadge);
    window.addEventListener('hashchange', routeGuard.handleHashChange);
    refreshBillingBadge();
    await routeGuard.accept();
  }

  function deactivate() {
    authAttempt++;
    if (listAdapter) {
      listAdapter.disconnect();
      listAdapter = null;
    }
    if (detailAdapter) {
      detailAdapter.disconnect();
      detailAdapter = null;
    }
    if (userAdapter) {
      userAdapter.disconnect();
      userAdapter = null;
    }
    if (billingAdapter) {
      billingAdapter.disconnect();
      billingAdapter = null;
    }
    if (platformCostsAdapter) {
      platformCostsAdapter.disconnect();
      platformCostsAdapter = null;
    }
    if (markInvoicedAdapter) {
      markInvoicedAdapter.disconnect();
      markInvoicedAdapter = null;
    }
    if (clientInvoiceAdapter) {
      clientInvoiceAdapter.disconnect();
      clientInvoiceAdapter = null;
    }
    sessionApi = null;
    renderBillingBadge(null);
    document.removeEventListener('billing-changed', refreshBillingBadge);
    if (clipboardAdapter) {
      clipboardAdapter.disconnect();
      clipboardAdapter = null;
    }
    if (menuClickHandler) {
      menuBtn.removeEventListener('click', menuClickHandler);
    }
    if (documentNavClickHandler) {
      document.removeEventListener('click', documentNavClickHandler);
    }
    const logoutButton = document.querySelector('#btn-logout');
    if (logoutButton && logoutClickHandler) {
      logoutButton.removeEventListener('click', logoutClickHandler);
    }
    menuClickHandler = null;
    documentNavClickHandler = null;
    logoutClickHandler = null;
    window.removeEventListener('hashchange', routeGuard.handleHashChange);
    routeGuard.reset();
    detailEl.prepareClient('');
    billingEl.addItem.closeForm();
    loginEl.reset();
    appEl.classList.add('hidden');
    loginEl.style.display = '';
    location.hash = '#/';
  }

  /** @param {string} hash an accepted route */
  async function showRoute(hash) {
    updateNav(hash);
    navEl.classList.remove('open');
    menuBtn.classList.remove('open');
    menuBtn.setAttribute('aria-expanded', 'false');

    const detailMatch = hash.match(CLIENT_DETAIL_ROUTE);

    if (hash === '#/users') {
      showOnly(userListEl);
      // Users first: their successful load clears an earlier error, which must not hide a failure
      // to load the filter options.
      await userAdapter.loadUsers();
      await userAdapter.loadClientOptions();
    } else if (hash === BILLING_ROUTE) {
      showOnly(billingEl);
      // Overview first: its successful load clears an earlier error, which must not hide a failure
      // to load the platform costs.
      await billingAdapter.showOverview();
      await platformCostsAdapter.showCosts();
    } else if (detailMatch) {
      showOnly(detailEl);
      await detailAdapter.loadClient(detailMatch[1]);
    } else {
      showOnly(listEl);
      await listAdapter.loadClients();
    }
  }

  const onLoginSubmit = (event) => {
    activate(event.detail);
  };

  document.addEventListener('login-submit', onLoginSubmit);

  return {
    destroy() {
      if (destroyed) {
        return;
      }
      destroyed = true;
      deactivate();
      document.removeEventListener('login-submit', onLoginSubmit);
    }
  };
}

if (!window.__WB_DISABLE_AUTO_BOOTSTRAP__) {
  bootstrap();
}
